import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Marker, Source, Layer, type MapRef } from '@vis.gl/react-maplibre';
import type { MapMouseEvent } from 'maplibre-gl';
import MapView from 'components/Map/MapView';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { latLonToUtm, utmToLatLon } from 'utils/utmToLatLon';
import { zoneGeogToGeoPoints } from 'utils/geohash';
import { useAppSelector } from 'store/hook';
import { selectLatestTagData } from 'store/device.slice';
import { selectAssets } from 'store/asset.slice';
import { images } from 'components/Asset/PNG';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import type { OtherLocation } from './LocationBoundaryDrawer';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';

// Replaces the flyTo altitude (metres) this map used under Cesium — there is
// no direct altitude→zoom formula, this zoom level was picked to match the
// old visual closeness for both the initial edit-pin center and the
// mine-site-average center.
const INITIAL_ZOOM = 16;

const LOCATION_TYPE_COLORS: Record<string, string> = {
  loading: '#4ade80',
  dumping: '#fbbf24',
  parking: '#a78bfa',
  workshop: '#94a3b8',
  fueling: '#fb923c',
  weighbridge: '#2dd4bf',
  crushing: '#f87171',
  crusher: '#f87171',
  processing: '#22d3ee',
  stockpile: '#e879f9',
  intersection: '#60a5fa',
};

function buildTruckLabel(bumperNumber: string, ignitionOn: boolean): string {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const dotColor = ignitionOn ? '#4ade80' : '#f87171';
  ctx.fillStyle = 'rgba(10,15,30,0.88)';
  ctx.strokeStyle = ignitionOn ? '#4ade80' : '#94a3b8';
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(11, canvas.height / 2, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, 20, canvas.height / 2);
  return canvas.toDataURL();
}

function buildPinIcon(colorHex: string): string {
  const w = 32;
  const h = 44;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const cx = w / 2;
  const cy = 14;
  const r = 12;
  ctx.fillStyle = colorHex;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI, 0, false);
  ctx.lineTo(cx + r * 0.6, cy + r * 0.9);
  ctx.quadraticCurveTo(cx, h - 2, cx - r * 0.6, cy + r * 0.9);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  return canvas.toDataURL();
}

function getAssetPng(assetType: string): string {
  const svgKey = mapVehiclesAssets(assetType);
  const pngKey =
    svgKey === 'haulTruckImage' ||
    svgKey === 'lightTruckImage' ||
    svgKey === 'heavyTruckImage'
      ? 'fallbackhaul_truckImageSmall'
      : 'fallbackloaderImageSmall';
  return images[pngKey] ?? '';
}

interface PickedPoint {
  latitude: number;
  longitude: number;
  easting: number;
  northing: number;
}

interface TruckDot {
  deviceId: string;
  lat: number;
  lon: number;
  bumper: string;
  ignitionOn: boolean;
  assetType: string;
}

interface LocationMapPickerProps {
  onPick: (easting: number, northing: number, lat: number, lon: number) => void;
  initialEasting?: number;
  initialNorthing?: number;
  proj4String: string | null;
  localOriginEasting: number | null;
  localOriginNorthing: number | null;
  otherLocations?: OtherLocation[];
}

const LocationMapPickerInner = ({
  onPick,
  initialEasting,
  initialNorthing,
  proj4String,
  localOriginEasting,
  localOriginNorthing,
  otherLocations,
}: LocationMapPickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const hasCenteredRef = useRef(false);
  const { flyTo } = useMapFlyTo(mapRef);

  const [mapReady, setMapReady] = useState(false);
  const [pickedPoint, setPickedPoint] = useState<PickedPoint | null>(null);
  const isMountedRef = useRef(false);

  const latestTagData = useAppSelector(selectLatestTagData);
  const assets = useAppSelector(selectAssets);

  // device → { bumperNumber, assetType }
  const assetInfoMap = useMemo<
    Record<string, { bumper: string; assetType: string }>
  >(() => {
    const map: Record<string, { bumper: string; assetType: string }> = {};
    assets.forEach((a) => {
      if (a.device?.deviceId) {
        map[a.device.deviceId] = {
          bumper: a.bumperNumber ?? a.device.deviceId,
          assetType: a.assetType ?? 'haul_truck',
        };
      }
    });
    return map;
  }, [assets]);

  // Extract live truck positions with bumper + ignition + assetType from Redux
  const truckDots = useMemo<TruckDot[]>(() => {
    return Object.entries(latestTagData)
      .map(([deviceId, s]) => {
        const lat = Number(s.gps_latitude);
        const lon = Number(s.gps_longitude);
        if (!isFinite(lat) || !isFinite(lon) || lat === 0 || lon === 0)
          return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        const info = assetInfoMap[deviceId];
        return {
          deviceId,
          lat,
          lon,
          bumper: info?.bumper ?? deviceId,
          ignitionOn: String(s.sb_ignition_status) === 'true',
          assetType: info?.assetType ?? 'haul_truck',
        };
      })
      .filter((d): d is TruckDot => d !== null);
  }, [latestTagData, assetInfoMap]);

  // Mine-site center = average of all truck GPS positions
  const mineSiteCenter = useMemo<{ lat: number; lon: number } | null>(() => {
    if (truckDots.length === 0) return null;
    const avgLat = truckDots.reduce((s, d) => s + d.lat, 0) / truckDots.length;
    const avgLon = truckDots.reduce((s, d) => s + d.lon, 0) / truckDots.length;
    return { lat: avgLat, lon: avgLon };
  }, [truckDots]);

  const pickedPinIcon = useMemo(
    () => buildPinIcon(theme.palette.primary.main),
    [theme.palette.primary.main]
  );

  const placePin = useCallback(
    (lat: number, lon: number) => {
      const utm = latLonToUtm(
        lat,
        lon,
        proj4String,
        localOriginEasting,
        localOriginNorthing
      );
      if (!utm) return;
      setPickedPoint({ latitude: lat, longitude: lon, ...utm });
      onPick(utm.easting, utm.northing, lat, lon);
    },
    [onPick, proj4String, localOriginEasting, localOriginNorthing]
  );

  // Click-to-drop-pin — replaces the Cesium ScreenSpaceEventHandler LEFT_CLICK
  // (pickEllipsoid + Cartographic) with MapLibre's native click event, which
  // already hands back lon/lat directly.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const handleClick = (e: MapMouseEvent) => {
      placePin(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [mapReady, placePin]);

  // On map ready: show initial pin (edit mode) or fly to mine-site center
  useEffect(() => {
    if (!mapReady || hasCenteredRef.current) return;
    if (initialEasting != null && initialNorthing != null) {
      const latLon = utmToLatLon(
        initialEasting,
        initialNorthing,
        proj4String,
        localOriginEasting,
        localOriginNorthing
      );
      if (latLon) {
        hasCenteredRef.current = true;
        setPickedPoint({
          latitude: latLon.latitude,
          longitude: latLon.longitude,
          easting: initialEasting,
          northing: initialNorthing,
        });
        flyTo(latLon.latitude, latLon.longitude, INITIAL_ZOOM);
        return;
      }
    }
    if (mineSiteCenter) {
      hasCenteredRef.current = true;
      flyTo(mineSiteCenter.lat, mineSiteCenter.lon, INITIAL_ZOOM);
    }
  }, [
    mapReady,
    initialEasting,
    initialNorthing,
    mineSiteCenter,
    flyTo,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  ]);

  // Live-update pin as user types easting/northing — debounced 400ms
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (!mapReady || initialEasting == null || initialNorthing == null) return;
    if (!isFinite(initialEasting) || !isFinite(initialNorthing)) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const latLon = utmToLatLon(
        initialEasting,
        initialNorthing,
        proj4String,
        localOriginEasting,
        localOriginNorthing
      );
      if (!latLon) return;
      setPickedPoint({
        latitude: latLon.latitude,
        longitude: latLon.longitude,
        easting: initialEasting,
        northing: initialNorthing,
      });
    }, 400);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [initialEasting, initialNorthing, mapReady]);

  // Truck markers: a small vertical stack (bumper label above, asset icon
  // below), one Marker per truck — same visual pairing as the old label +
  // billboard Entity pair, just as plain DOM now.
  const truckMarkers = useMemo(() => {
    return truckDots.map((dot) => {
      const labelImage = buildTruckLabel(dot.bumper, dot.ignitionOn);
      const pngImage = getAssetPng(dot.assetType);
      return (
        <Marker
          key={dot.deviceId}
          longitude={dot.lon}
          latitude={dot.lat}
          anchor="center"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={labelImage}
              sx={{ height: 17, mb: '2px' }}
            />
            <Box component="img" src={pngImage} sx={{ height: 28 }} />
          </Box>
        </Marker>
      );
    });
  }, [truckDots]);

  // Other saved locations, rendered for context: one shared GeoJSON source
  // carrying every zone polygon (colored per-feature via data-driven paint),
  // plus a small label Marker at each zone's centroid/dot.
  const otherLocationZonesGeoJson = useMemo(() => {
    const fallbackHex = theme.palette.primary.main;
    const features = (otherLocations ?? [])
      .map((loc) => {
        const hull = zoneGeogToGeoPoints(loc.zoneGeog);
        if (hull.length < 3) return null;
        const colorHex =
          LOCATION_TYPE_COLORS[loc.locationType?.toLowerCase()] ?? fallbackHex;
        const ring = hull.map((p) => [p.lon, p.lat]);
        ring.push(ring[0]);
        return {
          type: 'Feature' as const,
          properties: { fillColor: colorHex, outlineColor: colorHex },
          geometry: { type: 'Polygon' as const, coordinates: [ring] },
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
    return { type: 'FeatureCollection' as const, features };
  }, [otherLocations, theme.palette.primary.main]);

  const otherLocationLabels = useMemo(() => {
    const fallbackHex = theme.palette.primary.main;
    return (otherLocations ?? [])
      .map((loc) => {
        const colorHex =
          LOCATION_TYPE_COLORS[loc.locationType?.toLowerCase()] ?? fallbackHex;
        const hull = zoneGeogToGeoPoints(loc.zoneGeog);
        let labelLat = loc.centroidLat;
        let labelLon = loc.centroidLon;
        if (hull.length >= 3) {
          labelLat = hull.reduce((s, p) => s + p.lat, 0) / hull.length;
          labelLon = hull.reduce((s, p) => s + p.lon, 0) / hull.length;
        }
        if (labelLat == null || labelLon == null) return null;
        return (
          <Marker
            key={loc.id}
            longitude={labelLon}
            latitude={labelLat}
            anchor="bottom"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              {hull.length < 3 && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: colorHex,
                    border: '1px solid #fff',
                    mb: '2px',
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 1px 3px rgba(0,0,0,0.9)',
                  whiteSpace: 'nowrap',
                }}
              >
                {loc.name}
              </Typography>
            </Box>
          </Marker>
        );
      })
      .filter((el): el is NonNullable<typeof el> => el !== null);
  }, [otherLocations, theme.palette.primary.main]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {/* Map */}
      <Box
        sx={{
          height: 340,
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          cursor: 'crosshair',
        }}
      >
        {!mapReady && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              zIndex: 5,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        <MapView
          ref={mapRef}
          basemap={
            hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'
          }
          style={{ width: '100%', height: '100%' }}
          onLoad={() => {
            setMapReady(true);
          }}
        >
          {otherLocationZonesGeoJson.features.length > 0 && (
            <Source
              id="location-picker-other-zones"
              type="geojson"
              data={otherLocationZonesGeoJson}
            >
              <Layer
                id="location-picker-other-zones-fill"
                type="fill"
                paint={{
                  'fill-color': ['get', 'fillColor'],
                  'fill-opacity': 0.25,
                }}
              />
              <Layer
                id="location-picker-other-zones-line"
                type="line"
                paint={{
                  'line-color': ['get', 'outlineColor'],
                  'line-width': 2,
                  'line-opacity': 0.8,
                }}
              />
            </Source>
          )}
          {otherLocationLabels}
          {truckMarkers}

          {/* User-picked pin */}
          {pickedPoint && (
            <Marker
              longitude={pickedPoint.longitude}
              latitude={pickedPoint.latitude}
              anchor="bottom"
            >
              <Box
                component="img"
                src={pickedPinIcon}
                sx={{ height: 44, pointerEvents: 'none' }}
              />
            </Marker>
          )}
        </MapView>

        {/* Click hint */}
        {mapReady && !pickedPoint && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.85),
              backdropFilter: 'blur(4px)',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.4),
              zIndex: 4,
              pointerEvents: 'none',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('loc_importer_map_click_hint')}
            </Typography>
          </Box>
        )}

        {/* Picked coords badge */}
        {pickedPoint && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.9),
              zIndex: 4,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <MyLocationIcon sx={{ fontSize: 13, color: '#fff' }} />
            <Typography
              variant="caption"
              sx={{ color: '#fff', fontFamily: 'monospace', fontWeight: 600 }}
            >
              E: {pickedPoint.easting.toFixed(2)} · N:{' '}
              {pickedPoint.northing.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Truck count badge */}
        {truckDots.length > 0 && mapReady && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 4,
              px: 1,
              py: 0.4,
              borderRadius: 1.5,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.88),
              backdropFilter: 'blur(4px)',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.4),
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          ></Box>
        )}
      </Box>
    </Box>
  );
};

export const LocationMapPicker = React.memo(LocationMapPickerInner);
